import React, { useState } from 'react';
import { Globe, Mail } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Locale } from '../../i18n';
import { localeRedirectUrl, markSignInLocale } from '../../utils/localePreference';
import { supabase } from '../../lib/supabaseClient';
import BrandWordmark from '../common/BrandWordmark';
import './LoginScreen.scss';

interface LoginScreenProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ locale, onLocaleChange }) => {
  const { formatMessage: t } = useIntl();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genericAuthError = t({ id: 'auth.genericError' });

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    setError('');
    markSignInLocale(locale);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: localeRedirectUrl(window.location.origin, locale) },
    });
    if (err) {
      setError(genericAuthError);
    }
  };

  const sendCode = async () => {
    setError('');
    setLoading(true);
    markSignInLocale(locale);
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: localeRedirectUrl(window.location.origin, locale),
      },
    });
    setLoading(false);
    if (err) {
      setError(genericAuthError);
    } else {
      setSent(true);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendCode();
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: 'email',
    });
    setLoading(false);
    if (err) {
      setError(genericAuthError);
    }
  };

  const handleChangeEmail = () => {
    setSent(false);
    setCode('');
    setError('');
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandWordmark as="h1" className="login-card__title" />
        <p className="login-card__subtitle">{t({ id: 'auth.subtitle' })}</p>

        <label className="login-card__language">
          <div className="login-card__language-control">
            <Globe className="login-card__language-icon" size={18} strokeWidth={2} aria-hidden="true" />
            <select
              className="login-card__language-select"
              value={locale}
              onChange={event => {
                const nextLocale = event.target.value as Locale;
                markSignInLocale(nextLocale);
                onLocaleChange(nextLocale);
              }}
              aria-label={t({ id: 'auth.languageQuestion' })}
            >
              <option value="en">English</option>
              <option value="uk">Українська</option>
            </select>
          </div>
        </label>

        <button className="login-card__google-btn" onClick={() => void handleOAuthLogin('google')}>
          <span className="login-card__google-icon-wrap" aria-hidden="true">
            <svg className="login-card__google-icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </span>
          {t({ id: 'auth.signInGoogle' })}
        </button>

        <button className="login-card__google-btn login-card__apple-btn" onClick={() => void handleOAuthLogin('apple')}>
          <span className="login-card__google-icon-wrap" aria-hidden="true">
            <svg className="login-card__google-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.75 2.21 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.88.69 1.19-.02 1.94-1.08 2.66-2.15.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.31-.89-2.32-3.54zM14.2 6.13c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.55 1.31-.56.65-1.05 1.69-.92 2.69.97.07 1.96-.49 2.57-1.22z"/>
            </svg>
          </span>
          {t({ id: 'auth.signInApple' })}
        </button>

        <div className="login-card__divider">
          <span>{t({ id: 'auth.or' })}</span>
        </div>

        {sent ? (
          <form className="login-card__email-form" onSubmit={handleVerifyCode}>
            <p className="login-card__sent">
              <span className="login-card__sent-icon" aria-hidden="true">
                <Mail size={14} strokeWidth={2} />
              </span>
              <span>{t({ id: 'auth.magicLinkSent' })}</span>
            </p>
            <input
              className="login-card__email-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              placeholder={t({ id: 'auth.codePlaceholder' })}
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <button className="login-card__email-btn" type="submit" disabled={loading || code.trim().length === 0}>
              {loading ? t({ id: 'auth.verifying' }) : t({ id: 'auth.verifyCode' })}
            </button>
            {error && <p className="login-card__error">{error}</p>}
            <div className="login-card__inline-actions">
              <button type="button" className="login-card__text-btn" onClick={handleChangeEmail}>
                {t({ id: 'auth.changeEmail' })}
              </button>
              <button type="button" className="login-card__text-btn" onClick={() => void sendCode()} disabled={loading}>
                {t({ id: 'auth.resendCode' })}
              </button>
            </div>
          </form>
        ) : (
          <form className="login-card__email-form" onSubmit={handleSendCode}>
            <input
              className="login-card__email-input"
              type="email"
              required
              placeholder={t({ id: 'auth.emailPlaceholder' })}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <button className="login-card__email-btn" type="submit" disabled={loading}>
              {loading ? t({ id: 'auth.sending' }) : t({ id: 'auth.sendMagicLink' })}
            </button>
            {error && <p className="login-card__error">{error}</p>}
          </form>
        )}

        <p className="login-card__legal">
          {t({ id: 'legal.agreePrefix' })}{' '}
          <a href="/terms">{t({ id: 'legal.terms' })}</a>{' '}
          {t({ id: 'legal.and' })}{' '}
          <a href="/privacy">{t({ id: 'legal.privacy' })}</a>.
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
